import { isProtectionEnabled } from '../protectionStatus';
import { isYoutubeVideo, fetchYoutubeMetadata } from './youtubeNavigation';
import { getVerdict } from './verdict';
import { ProcessUrlResponse } from '../types';
import { redirectToURL } from '../actions/navigationActions';
import {
  redirectToBlockedByCategoryScreen,
  parseBlockerPageUrl,
} from './blockingUtils';
import { getSafeUrl } from '../safeSearch/safeSearch';
import { getBlockedScreenUrl } from '../chrome/routing';
import { reportAndUpdate, syncRules } from '../actions/engineActions';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { isDelegationStopped } from '../delegation/delegation';
import { WebEvent } from '../types/api/events';

export const processWebNavigation = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => {
  // Do nothing if we should ignore the request (protection disabled, not the main document...)
  if (shouldIgnore(details)) {
    return;
  }

  // Wait for the verdict and apply it
  return getVerdict(details.url).then(processedVerdict => {
    const originalUrl = details.url;
    console.log(
      `New web event. url = ${originalUrl}, QVerdict = ${
        ProcessUrlResponse[processedVerdict.verdict]
      }, requestId = ${processedVerdict.requestId}`
    );

    // Ignored verdicts should not be reported or blocked
    if (processedVerdict.verdict === ProcessUrlResponse.IGNORE) {
      console.log(`Ignoring navigation. url = ${originalUrl}`);
      return;
    }

    // Report it if needed
    if (processedVerdict.webEvent) {
      updateWebEventMetadataIfNeeded(processedVerdict.webEvent).then(webEvent =>
        reportAndUpdate(webEvent)
      );
    }

    // Block by time if needed
    if (shouldBlockByTime(processedVerdict.verdict)) {
      const blockedByTimeScreenUrl = getBlockedScreenUrl(
        processedVerdict.verdict
      );
      if (blockedByTimeScreenUrl) {
        console.log(`Blocking by time. url = ${originalUrl}`);
        return blockByTime(details.tabId, blockedByTimeScreenUrl, originalUrl);
      }
    }

    // Show blocker if needed
    if (processedVerdict.verdict === ProcessUrlResponse.BLOCK) {
      console.log(`Blocking by category. url = ${originalUrl}`);
      return redirectToBlockedByCategoryScreen(details.tabId, originalUrl);
    }

    // Redirect to safe search if needed
    if (
      processedVerdict.verdict === ProcessUrlResponse.REDIRECT_TO_SAFE_SEARCH
    ) {
      console.log(`Redirecting to Safe Search. url = ${originalUrl}`);
      return redirectToURL(details.tabId, getSafeUrl(originalUrl));
    }
  });
};

const updateWebEventMetadataIfNeeded = async (webEvent: WebEvent) => {
  if (
    isYoutubeVideo(webEvent.url) &&
    (!webEvent.video_title || !webEvent.video_channel_title)
  ) {
    const metadata = await fetchYoutubeMetadata(webEvent.url);
    Object.assign(webEvent, {
      video_title: metadata.title,
      video_channel_title: metadata.author_name,
    });
  }
  return webEvent;
};

const blockByTime = async (
  tabId: number,
  screenUrl: string,
  originalUrl: string
) => {
  const finalUrl = parseBlockerPageUrl(screenUrl, originalUrl);
  const redirected = await redirectToURL(tabId, finalUrl);
  if (redirected) {
    void syncRules();
  }
  return redirected;
};

const shouldIgnore = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => {
  return (
    !isMainDocument(details) ||
    !isProtectionEnabled() ||
    isSafeNetworkDelegated() ||
    isDelegationStopped()
  );
};

const shouldBlockByTime = (verdict: ProcessUrlResponse) =>
  verdict === ProcessUrlResponse.BLOCK_BY_QUOTA ||
  verdict === ProcessUrlResponse.BLOCK_BY_SCHEDULE;

const isMainDocument = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => details.frameId === 0;
